import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import {
  getOrCreateDeviceKeypair,
  importPublicKeySpki,
  type DeviceKeypair,
} from "../utils/deviceKeys";
import {
  setCurrentDevice,
  setFamilyKeyInMemory,
  getFamilyKeyInMemory,
  hasFamilyKeyInMemory,
  clearFamilyKeysInMemory,
} from "../utils/sessionKeys";
import {
  generateFamilyKey,
  wrapFamilyKeyForDevice,
  unwrapFamilyKey,
} from "../utils/familyKeyCrypto";
import { aesEncrypt, aesDecrypt, isEncryptedPayload } from "../utils/encryption";
import { E2EENotReadyError } from "../utils/writeEncryption";
import logger from "../utils/logger";

/**
 * E2EE orchestration hook (device-bound, ECDH model).
 *
 * Lifecycle on each user/family change:
 *   1. Load or generate this browser's ECDH keypair (per Clerk user).
 *   2. Register the device on Convex (`userDevices`).
 *   3. Subscribe to `getFamilyKeyForDevice` — when a row arrives, unwrap and cache.
 *   4. If I'm parent_a and no family key exists yet → bootstrap one (self-wrap).
 *   5. Reactively wrap+upload family key for any other device in the family
 *      that doesn't have one yet.
 */
export function useEncryption() {
  const { user } = useAuthStore();
  const { family, members } = useFamilyStore();

  const [device, setDevice] = useState<DeviceKeypair | null>(null);
  const [familyKeyReady, setFamilyKeyReady] = useState<boolean>(false);

  const bootstrappingRef = useRef(false);
  const wrappingInFlight = useRef<Set<string>>(new Set());

  const registerDevice = useMutation(api.encryption.mutations.registerDevice);
  const bootstrapFamilyKey = useMutation(api.encryption.mutations.bootstrapFamilyKey);
  const storeKeyForDevice = useMutation(
    api.encryption.mutations.storeFamilyKeyForDevice
  );
  const activateEncryption = useMutation(
    api.encryption.mutations.activateEncryption
  );

  // ── 1+2. Device keypair: load/create, register on Convex ─────────────
  useEffect(() => {
    if (!user) {
      setCurrentDevice(null);
      setDevice(null);
      clearFamilyKeysInMemory();
      setFamilyKeyReady(false);
      bootstrappingRef.current = false;
      wrappingInFlight.current.clear();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const kp = await getOrCreateDeviceKeypair(user.id);
        if (cancelled) return;
        setCurrentDevice(kp);
        setDevice(kp);
        await registerDevice({
          deviceId: kp.deviceId,
          publicKey: kp.publicKeySpkiBase64,
          label: kp.label ?? undefined,
        });
      } catch (err) {
        logger.error("[E2EE] device init failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, registerDevice]);

  // ── Reactive subscriptions ──────────────────────────────────────────
  const familyKeyRow = useQuery(
    api.encryption.queries.getFamilyKeyForDevice,
    family && device
      ? {
          familyId: family.id as Id<"families">,
          deviceId: device.deviceId,
        }
      : "skip"
  );

  const allDevices = useQuery(
    api.encryption.queries.listFamilyDevices,
    family ? { familyId: family.id as Id<"families"> } : "skip"
  );

  // ── 3. Unwrap family key when row arrives for my device ─────────────
  useEffect(() => {
    if (!family || !device) return;
    if (familyKeyRow === undefined) return; // still loading
    if (familyKeyRow === null) {
      // No row yet — keep current state (might be bootstrap or wait)
      if (!hasFamilyKeyInMemory(family.id)) setFamilyKeyReady(false);
      return;
    }
    if (hasFamilyKeyInMemory(family.id)) {
      setFamilyKeyReady(true);
      return;
    }
    if (!allDevices) return; // need device list to find sender's public key

    let cancelled = false;
    (async () => {
      try {
        const senderDeviceId = familyKeyRow.wrappedBy ?? familyKeyRow.deviceId;

        let senderPublicSpki: string;
        if (senderDeviceId === device.deviceId) {
          // Self-wrap (bootstrap)
          senderPublicSpki = device.publicKeySpkiBase64;
        } else {
          const sender = allDevices.find((d) => d.deviceId === senderDeviceId);
          if (!sender) {
            logger.error("[E2EE] sender device not found:", senderDeviceId);
            return;
          }
          senderPublicSpki = sender.publicKey;
        }

        const senderPub = await importPublicKeySpki(senderPublicSpki);
        const familyKey = await unwrapFamilyKey(
          familyKeyRow.encryptedKey,
          device.privateKey,
          senderPub
        );
        if (cancelled) return;

        setFamilyKeyInMemory(family.id, familyKey);
        setFamilyKeyReady(true);
        logger.debug("[E2EE] unwrapped family key for device", device.deviceId);
      } catch (err) {
        logger.error("[E2EE] unwrap failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [family, device, familyKeyRow, allDevices]);

  // ── 4. Bootstrap (parent_a, no keys anywhere yet) ───────────────────
  useEffect(() => {
    if (!user || !family || !device || !allDevices) return;
    if (bootstrappingRef.current) return;
    if (hasFamilyKeyInMemory(family.id)) return;
    if (familyKeyRow !== null && familyKeyRow !== undefined) return;

    const myMembership = members.find((m) => m.user_id === user.id);
    if (myMembership?.role !== "parent_a") return;

    // Need my own device registered before bootstrap
    const myDeviceListed = allDevices.some(
      (d) => d.deviceId === device.deviceId
    );
    if (!myDeviceListed) return;

    const anyKeyExists = allDevices.some((d) => d.hasFamilyKey);
    if (anyKeyExists) return;

    bootstrappingRef.current = true;
    (async () => {
      try {
        const familyKey = await generateFamilyKey();
        const wrapped = await wrapFamilyKeyForDevice(
          familyKey,
          device.privateKey,
          device.publicKey
        );
        await bootstrapFamilyKey({
          familyId: family.id as Id<"families">,
          deviceId: device.deviceId,
          encryptedKey: wrapped,
          version: 1,
        });
        setFamilyKeyInMemory(family.id, familyKey);
        setFamilyKeyReady(true);
        logger.debug("[E2EE] bootstrapped family key");
      } catch (err) {
        logger.error("[E2EE] bootstrap failed:", err);
        bootstrappingRef.current = false; // allow retry next render
      }
    })();
  }, [
    user,
    family,
    device,
    allDevices,
    members,
    familyKeyRow,
    bootstrapFamilyKey,
  ]);

  // ── 5. Auto-transfer: wrap family key for any device that lacks one ─
  useEffect(() => {
    if (!family || !device || !allDevices) return;
    if (!hasFamilyKeyInMemory(family.id)) return;

    const familyKey = getFamilyKeyInMemory(family.id);
    if (!familyKey) return;

    const needTransfer = allDevices.filter(
      (d) => !d.hasFamilyKey && d.deviceId !== device.deviceId
    );
    if (needTransfer.length === 0) return;

    (async () => {
      for (const target of needTransfer) {
        if (wrappingInFlight.current.has(target.deviceId)) continue;
        wrappingInFlight.current.add(target.deviceId);

        try {
          const theirPub = await importPublicKeySpki(target.publicKey);
          const wrapped = await wrapFamilyKeyForDevice(
            familyKey,
            device.privateKey,
            theirPub
          );
          await storeKeyForDevice({
            familyId: family.id as Id<"families">,
            targetUserId: target.userId,
            targetDeviceId: target.deviceId,
            encryptedKey: wrapped,
            wrappedByDeviceId: device.deviceId,
            version: 1,
          });
          logger.debug("[E2EE] transferred key to", target.deviceId);
          // Keep deviceId in wrappingInFlight — next render of allDevices will
          // show hasFamilyKey:true so we won't retry. If something goes wrong
          // and the row never appears, we'd never retry — accept that.
        } catch (err) {
          logger.error("[E2EE] transfer failed for", target.deviceId, err);
          wrappingInFlight.current.delete(target.deviceId); // allow retry
        }
      }
    })();
  }, [family, device, allDevices, storeKeyForDevice]);

  // ── 6. Activate encryption flag when ALL family devices have keys ───
  const activationInFlightRef = useRef(false);
  useEffect(() => {
    if (!family || !allDevices) return;
    if (family.encryption_active_since) return; // already active
    if (activationInFlightRef.current) return;
    if (allDevices.length === 0) return;
    if (!allDevices.every((d) => d.hasFamilyKey)) return;

    activationInFlightRef.current = true;
    (async () => {
      try {
        await activateEncryption({ familyId: family.id as Id<"families"> });
        logger.debug("[E2EE] encryption activated for family", family.id);
      } catch (err) {
        logger.error("[E2EE] activation failed:", err);
        activationInFlightRef.current = false;
      }
    })();
  }, [family, allDevices, activateEncryption]);

  // ── Public API ──────────────────────────────────────────────────────
  const encrypt = useCallback(
    async (plaintext: string): Promise<string | null> => {
      if (!family) return null;
      // Bypass: nie szyfruj dopóki rodzina nie aktywowała E2EE
      // (czyli dopóki nie WSZYSCY członkowie mają klucz na swoich urządzeniach).
      if (!family.encryption_active_since) return null;
      const key = getFamilyKeyInMemory(family.id);
      // Active + brak klucza = nowe urządzenie czeka na transfer — odmawiamy zapisu,
      // żeby plaintext nie wszedł do rodziny w której wszystko inne jest zaszyfrowane.
      if (!key) throw new E2EENotReadyError();
      return await aesEncrypt(plaintext, key);
    },
    [family]
  );

  const decrypt = useCallback(
    async (ciphertext: string): Promise<string | null> => {
      if (!family) return null;
      const key = getFamilyKeyInMemory(family.id);
      if (!key) return null;
      try {
        return await aesDecrypt(ciphertext, key);
      } catch {
        return null;
      }
    },
    [family]
  );

  const decryptIfNeeded = useCallback(
    async (value: string): Promise<string> => {
      if (!isEncryptedPayload(value)) return value;
      const decrypted = await decrypt(value);
      return decrypted ?? value;
    },
    [decrypt]
  );

  // Compose readiness: have a family key in memory
  const isReady = familyKeyReady && family !== null && hasFamilyKeyInMemory(family.id);

  return {
    isReady,
    device,
    devicesInFamily: allDevices ?? null,
    encrypt,
    decrypt,
    isEncrypted: isEncryptedPayload,
    decryptIfNeeded,
  };
}
